import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { useAuth } from "../../components/shared/AuthContext";
import { DatePicker, LogoLoader } from "../../components/ui";
import useVoucherDateLock from "../../hooks/useVoucherDateLock";
// receipt_voucher.amount stores PAISE; the form works in RUPEES.
import { toPaise, toRupees, formatINR } from "../../utils/money";

const J = { primary: "#3F51B5", accent: "#2196F3", light: "#c3dbeb", lightBg: "#EEF2FB", text: "#1a237e" };
const TODAY = () => new Date().toISOString().split("T")[0];

// ONE field: Ledger. It names where the money CAME FROM; Mode of Receiving names
// where it LANDS. Every receipt is that pair:
//
//     Dr <the MOR account>    Cr <the chosen ledger>
//
// "Receipt Type" used to name an account GROUP, which was the same thing wearing
// a worse name — "Receipt Type = Share Capital" was always saying the money came
// out of Share Capital. So the group layer is gone and the dropdown lists the
// individual accounts.
//
// THE ALLOWLIST THAT USED TO BE HERE IS GONE, DELIBERATELY. It existed because a
// group with no posting branch saved a receipt, reported success and wrote zero
// ledger rows — Share Capital did exactly that until 2026-09-01. Removing it is
// safe ONLY because postReceiptToLedger now posts for any source account: there
// is no longer such a thing as a ledger without a rule. If that generic rule is
// ever narrowed again, this list has to come back with it.
const LEGACY_GROUP_TYPES = ["BankAccount", "DirectIncome", "DirectExpenses"];

// The one receipt that is NOT "money arrived out of a ledger". It posts
//   Dr Member Savings / Cr Borrowings from Members
// and moves no cash at all — the money is already in the drawer and the entry
// records the decision to lend it. It has no single source account to pick, so it
// stays a named option rather than a ledger. Must match MEMBER_BORROWING_TYPE in
// services/ledgerPosting.js, which dispatches on this exact string BEFORE the
// generic rule and would otherwise debit Cash for money that never moved.
const MEMBER_BORROWING_TYPE = "Borrowings from Members";

// Groups whose accounts belong to a MEMBER, so the member search still applies
// and the outstanding auto-fetch still runs. Everything else targets a
// branch-level account chosen from a dropdown.
const MEMBER_GROUPS = ["member savings", "loans outstanding"];

// NOTE: the backend dispatches on the stored receipt_type string —
// services/ledgerPosting.js posts both ledger legs when it reads "Borrowings".
// Renaming that group in base_ledger_group silently stops the posting.
const MOR_OPTIONS = ["CASH", "BANK", "UPI", "CHEQUE"];

// Funding that cannot arrive as physical cash.
//
// Every loan disbursement is paid FROM the bank — all 522 on the live database,
// because the payout payload carries no cash_type at all and ledgerPosting.js
// reads an absent mode as a bank payment. So a borrowing recorded as CASH lets
// the bank pay out money it never received: Rameswaram reached a bank balance of
// −95,640 exactly this way, with two 1,00,000 receipts booked to cash and a
// 95,640 payout taken from an empty bank account.
//
// Only CASH is removed. BANK/UPI/CHEQUE all settle to the bank already
// (ledgerPosting.js:817 treats anything but the literal "CASH" as bank), and an
// agency drawdown often arrives by UPI or cheque.
//
// "Borrowings from Members" is deliberately NOT here. It posts
// Dr Member Savings / Cr Borrowings from Members and touches neither cash nor
// bank, so no money arrives and the restriction would state something false.
// Its mode still matters for a different reason — the per-channel cap enforced
// in postMemberBorrowing — but any mode remains selectable.
const BANK_ONLY_RECEIPT_TYPES = ["Borrowings", "Share Capital"];

const EMPTY_FORM = {
  receipt_no: "", date: TODAY(),
  mor: "", receipt_type: "", other_description: "",
  ledger_id: "", ledger_name: "", ledger_type: "", member_unique_id: "",
  loan_outstanding: "", amount: "", narration: "",
};

const Receipts = () => {
  const { data: authData, roleName, jwt } = useAuth();
  const branchId = authData?.branchId || null;
  const isSuperAdmin = roleName === "superadmin";
  // A branch admin may also correct a past-dated receipt (assertVoucherDateAllowed
  // allows receipt_voucher edits for role "admin"). Normalised like the server.
  const isBranchAdmin = String(roleName || "").toLowerCase().replace(/\s+/g, "") === "admin";

  // The day a blocker is forcing this receipt to be dated, or null. Read from
  // the server rather than from the button that navigated here, so the sidebar
  // route and the popup route lock identically.
  const { lockDate } = useVoucherDateLock();

  const [receipts, setReceipts] = useState([]);
  const [members, setMembers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [accountGroups, setAccountGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  // Open but not changeable: a past receipt anyone may read and only an admin
  // or superadmin may alter.
  const [viewOnly, setViewOnly] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [showSearch, setShowSearch] = useState(false);
  const [ledgerQuery, setLedgerQuery] = useState("");
  const [search, setSearch] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [outstandingLoading, setOutstandingLoading] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      // For vouchers: filter by branchId inside JSON filter
      const voucherFilter = isSuperAdmin ? {} : (branchId ? { branchId } : {});
      const vf = JSON.stringify(voucherFilter);

      // For members/staff/ledgers: use API's built-in branchId param (safe – ignores null)
      const branchParam = (!isSuperAdmin && branchId) ? { branchId } : {};

      // Reads ledger_account under base_ledger_group, NOT ledger_group. That
      // table was built but never populated (0 rows), so the picker silently
      // offered nothing while 95 real accounts existed — the same wrong pair
      // Balancesheet.js and LedgerReport.js read before they were corrected.
      const [rvRes, mbRes, laRes, bgRes, stRes] = await Promise.all([
        axios.get("/api/getModelData/receipt_voucher", { baseURL: API_BASE_URL, params: { filter: vf } }),
        axios.get("/api/getModelData/personal_details", { baseURL: API_BASE_URL, params: { filter: '{}', ...branchParam } }),
        axios.get("/api/getModelData/ledger_account", { baseURL: API_BASE_URL, params: { filter: '{}' } }),
        axios.get("/api/getModelData/base_ledger_group", { baseURL: API_BASE_URL, params: { filter: '{}' } }),
        axios.get("/api/getModelData/staff_details", { baseURL: API_BASE_URL, params: { filter: '{}', ...branchParam } }),
      ]);
      const toArr = r => Array.isArray(r.data?.data) ? r.data.data : Array.isArray(r.data) ? r.data : [];

      const groups = toArr(bgRes);
      // Branch-scoped for ordinary users; shared accounts (branchId NULL) belong
      // to no single branch and stay visible to everyone.
      const accounts = toArr(laRes)
        .filter(a => isSuperAdmin || !branchId || a.branchId == null || Number(a.branchId) === Number(branchId))
        .map(a => {
          const g = groups.find(x => Number(x.id) === Number(a.ledger_id));
          return { ...a, groupName: g?.group_name || "", groupType: g?.group_type || "" };
        });

      setReceipts(toArr(rvRes));
      setMembers(toArr(mbRes));
      setLedgers(accounts);
      setAccountGroups(groups);
      setStaff(toArr(stRes));
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  // The destination account, decided by Mode of Receiving. Named here so it can
  // be removed from the source list: money cannot arrive in the same account it
  // left, and postReceiptToLedger refuses that pair — better not to offer it.
  const destinationGroup = String(formData.mor || "").trim().toUpperCase() === "CASH"
    ? "cash in hand"
    : "bank accounts";

  // EVERY ledger at this branch is a valid source, because postReceiptToLedger
  // posts for any of them. Already branch-scoped in loadAll.
  //
  // The legacy group filter stays: those five groups (SBI, SOFTWARE, BUS FARE,
  // Dhanalakshmi, ' Cash') carry group_type values outside every Balance Sheet
  // bucket, so a receipt against one would post a pair that no financial
  // statement picks up.
  const ledgerOptions = ledgers
    .filter(l => !LEGACY_GROUP_TYPES.includes(String(l.groupType || "").trim()))
    .filter(l => String(l.groupName || "").trim().toLowerCase() !== destinationGroup)
    .slice()
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));

  // "Borrowings from Members" is selected as a literal, not as a ledger id —
  // it has no source account. Everything else stores the chosen account's id.
  const isMemberBorrowing = formData.receipt_type === MEMBER_BORROWING_TYPE;

  const selectedLedger = ledgers.find(l => String(l.id) === String(formData.ledger_id)) || null;
  const selectedGroup = String(selectedLedger?.groupName || "").trim().toLowerCase();
  const isMemberGroup = !isMemberBorrowing && MEMBER_GROUPS.includes(selectedGroup);
  // The source is a Cash or Bank account, so the balance shown beside it is the
  // branch's own money rather than a member's outstanding.
  const isOwnFundsSource = selectedGroup === "cash in hand" || selectedGroup === "bank accounts";

  // Build combined search list: accounts, members, staff
  const searchEntries = [
    ...ledgers.map(l => ({
      id: l.id, label: l.name, sub: l.groupName || "Account", type: "ledger", unique_id: "",
    })),
    ...members.map(m => ({
      id: m.id,
      label: m.name || `Member #${m.id}`,
      sub: m.customerUniqueId || "",
      type: "member",
      unique_id: m.customerUniqueId || "",
    })),
    ...staff.map(s => ({
      id: s.id,
      label: s.name || `Staff #${s.id}`,
      sub: s.designation || "",
      type: "staff",
      unique_id: String(s.staffId || s.id),
    })),
  ].filter(e => e.label.toLowerCase().includes(ledgerQuery.toLowerCase()) ||
    e.sub.toLowerCase().includes(ledgerQuery.toLowerCase()));

  const nextReceiptNo = () => {
    const nums = receipts.map(r => parseInt(r.receipt_no) || 0);
    return String((nums.length ? Math.max(...nums) : 0) + 1);
  };

  // A receipt dated before today records money that arrived on a day already
  // reported. Correcting it is an admin or superadmin action, not routine entry.
  const isPastVoucher = item => Boolean(item?.date) && item.date < TODAY();
  // A receipt created by an Inter-Branch Transfer is changed only through that
  // payment at the sending branch (the server refuses it here too).
  const isLinked = item => Boolean(item?.linked_payment_id);
  const canEdit = item => !isLinked(item) && (isSuperAdmin || isBranchAdmin || !isPastVoucher(item));

  // Admin (own branch) or superadmin. The server re-checks both, and refuses a
  // delete that would take Cash or Bank below zero.
  const canDelete = isSuperAdmin || isBranchAdmin;
  const deleteReceipt = async (item) => {
    const amount = (Number(item.amount) || 0) / 100;   // PAISE column
    const ok = window.confirm(
      `Delete receipt RV-${item.receipt_no} dated ${item.date} for ` +
      `Rs.${amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}?\n\n` +
      "Its ledger entries are removed too. This cannot be undone."
    );
    if (!ok) return;
    try {
      await axios.post("/api/receipt-voucher/delete", { id: item.id }, {
        baseURL: API_BASE_URL,
        headers: { "x-token": jwt },
      });
      await loadAll();
    } catch (err) {
      alert(err.response?.data?.error || "Could not delete the receipt.");
    }
  };

  const openAdd = () => {
    setEditingItem(null);
    setViewOnly(false);
    // Opens ON the blocked day, not merely refusing every other day — the
    // operator came here to record that day's funding.
    setFormData({
      ...EMPTY_FORM,
      receipt_no: nextReceiptNo(),
      date: lockDate || TODAY(),
    });
    setShowModal(true);
  };

  const openEdit = item => {
    // A past receipt still OPENS — it is only read-only. Refusing to open it
    // would hide the record from the person trying to understand what was
    // entered, which is the opposite of what a locked book should do: history
    // stays visible, it just stops being rewritable.
    setViewOnly(!canEdit(item));
    setEditingItem(item);
    setFormData({
      receipt_no: item.receipt_no || "",
      date: item.date || TODAY(),
      mor: item.mor || "",
      receipt_type: item.receipt_type || "",
      other_description: item.other_description || "",
      ledger_id: item.ledger_id || "",
      ledger_name: item.ledger_name || "",
      ledger_type: item.ledger_type || "",
      member_unique_id: item.member_unique_id || "",
      loan_outstanding: item.loan_outstanding || "",
      // The stored value is PAISE; the input box is in RUPEES. Without this the
      // box would show 1234567891 for a Rs.1,23,45,678.91 receipt, and saving it
      // again would multiply by 100 a second time.
      amount: item.amount ? String(toRupees(item.amount)) : "",
      narration: item.narration || "",
    });
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditingItem(null); setViewOnly(false); };
  const set = (k, v) => setFormData(p => ({ ...p, [k]: v }));

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        type: editingItem ? "Edit" : "Add",
        model: "receipt_voucher",
        ...formData,
        // The operator types RUPEES; receipt_voucher.amount stores PAISE.
        // toPaise rounds rather than truncating — 12345.67 * 100 is
        // 1234566.9999999999 in binary floating point, so a truncate here would
        // quietly drop a paisa on ordinary values.
        loan_outstanding: parseFloat(formData.loan_outstanding) || 0,
        amount: toPaise(formData.amount),
        // A superadmin has no branch of their own, so `branchId` here is null.
        // On an Edit that would blank the voucher's branch and the ledger posting
        // fails with "could not determine the branch for this receipt" — the
        // stored row already knows which branch it belongs to, so keep it.
        branchId: editingItem?.branchId ?? branchId,
      };
      if (editingItem) payload.id = editingItem.id;
      // Identifies the caller. Without it the server sees an anonymous request:
      // identifyCaller (middleware.js) leaves req.user undefined, so
      // assertVoucherDateAllowed judges even a superadmin as "not superadmin"
      // and refuses every past-dated edit — the capability existed but was
      // unreachable from this page. Same header every other page sends.
      await axios.post("/api/addDetails", payload, {
        baseURL: API_BASE_URL,
        headers: { "x-token": jwt },
      });

      await loadAll();
      closeModal();
    } catch (err) {
      const message = err.response?.data?.error;
      alert(message || "Error saving receipt. Please try again.");
    }
    setLoading(false);
  };

  const fetchOutstanding = async (entry) => {
    setOutstandingLoading(true);
    try {
      if (entry.type === "member" || entry.type === "staff" || entry.type === "ledger") {
        // Replicate LedgerReport calculation:
        // Fetch all receipts and payments for this entry, then compute running balance
        const [rvRes, pvRes] = await Promise.all([
          axios.get("/api/getModelData/receipt_voucher", {
            baseURL: API_BASE_URL,
            params: { filter: JSON.stringify({ ledger_id: entry.id, ledger_type: entry.type }) }
          }),
          axios.get("/api/getModelData/payment_voucher", {
            baseURL: API_BASE_URL,
            params: { filter: JSON.stringify({ ledger_id: entry.id, ledger_type: entry.type }) }
          }),
        ]);
        const toArr = r => Array.isArray(r.data?.data) ? r.data.data : Array.isArray(r.data) ? r.data : [];
        const receipts = toArr(rvRes);
        const payments = toArr(pvRes);

        // Build combined rows sorted by date (same as LedgerReport)
        // Units differ between these fields and must be reconciled here:
        //   amount / outstanding_amount  -> PAISE (converted in Stage 2)
        //   loan_outstanding             -> RUPEES (still a rupee column)
        // The result feeds the rupee-denominated loan_outstanding input below,
        // so everything is normalised to rupees. Mixing them would make one
        // side of the subtraction 100x the other.
        const rows = [
          ...payments.map(p => ({ date: p.date, debit: toRupees(p.amount), credit: 0, outstanding: toRupees(p.outstanding_amount) })),
          ...receipts.map(r => ({ date: r.date, debit: 0, credit: toRupees(r.amount), outstanding: Number(r.loan_outstanding || 0) })),
        ].sort((a, b) => (a.date || "").localeCompare(b.date || ""));

        // Opening balance = first row's outstanding field
        const openingBal = rows.length > 0 ? rows[0].outstanding : 0;
        // Closing balance = opening + all debits - all credits
        const totalDebit = rows.reduce((s, r) => s + r.debit, 0);
        const totalCredit = rows.reduce((s, r) => s + r.credit, 0);
        const closing = openingBal + totalDebit - totalCredit;

        setFormData(p => ({ ...p, loan_outstanding: closing > 0 ? String(closing.toFixed(2)) : "" }));
      }
    } catch (err) {
      console.log("Outstanding fetch error:", err);
    }
    setOutstandingLoading(false);
  };

  // The source account's current balance, shown read-only beside the Ledger
  // picker so the operator can see what is there before moving it.
  //
  // Reads /ledger/funds-available rather than the voucher-walking fetchOutstanding
  // above: a Cash or Bank account's real balance lives in ledger_transaction, and
  // fetchOutstanding only sums receipt and payment vouchers, so for these accounts
  // it would report a confident wrong number — EMI collections, disbursements and
  // member withdrawals all move cash without writing a voucher.
  const fetchAccountBalance = async (groupName) => {
    if (!branchId) return;
    setOutstandingLoading(true);
    try {
      const res = await axios.get("/api/ledger/funds-available", {
        baseURL: API_BASE_URL, params: { branchId },
      });
      const d = res.data?.data || {};
      const isCashSource = String(groupName || "").trim().toLowerCase() === "cash in hand";
      const bal = Number(isCashSource ? d.cash : d.bank);
      setFormData(p => ({
        ...p,
        loan_outstanding: Number.isFinite(bal) ? String(bal.toFixed(2)) : "",
      }));
    } catch (err) {
      // Non-fatal: the balance is information, not a precondition. Leaving it
      // blank is honest; a stale or zero figure would not be.
      console.log("Account balance fetch error:", err);
      setFormData(p => ({ ...p, loan_outstanding: "" }));
    }
    setOutstandingLoading(false);
  };

  const selectLedger = entry => {
    setFormData(p => ({
      ...p,
      ledger_id: entry.id,
      ledger_name: entry.label,
      ledger_type: entry.type,
      member_unique_id: entry.unique_id,
      loan_outstanding: "",
    }));
    setShowSearch(false);
    setLedgerQuery("");
    fetchOutstanding(entry);
  };

  // Table pagination
  const filtered = receipts.filter(r =>
    (r.receipt_no || "").includes(search) ||
    (r.ledger_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (r.receipt_type || "").toLowerCase().includes(search.toLowerCase())
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const paginated = filtered.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const fmt = d => {
    if (!d) return "—";
    const [y, m, dd] = d.split("-"); return `${dd}-${m}-${y}`;
  };

  const receiptTypeBadge = (type, otherDesc) => {
    const colors = {
      "EMI Collection": "#27ae60",
      "Interest Payment": "#2980b9",
      "Processing Fee": "#8e44ad",
      "Penalty / Fine": "#c0392b",
      "Savings Collection": "#16a085",
      "Loan Pre-closure": "#d35400",
      "Other": "#7f8c8d",
    };
    const label = (type === "Other" && otherDesc) ? otherDesc : (type || "—");
    return (
      <span style={{
        backgroundColor: colors[type] || "#7f8c8d",
        color: "#fff", borderRadius: 4,
        padding: "2px 8px", fontSize: "0.8rem",
        maxWidth: 140, display: "inline-block",
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
      }} title={label}>{label}</span>
    );
  };

  return (
    <div style={{ background: "transparent", minHeight: "100vh" }}>
      {/* ── Header ── */}
      <div style={{ background: "transparent", padding: "4px 0", marginBottom: 20, boxShadow: "none" }}>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h5 className="mb-0 fw-bold" style={{ color: "#004BB5", fontSize: "1.15rem" }}>
              <i className="fas fa-receipt me-2" />Receipts
            </h5>
          </div>
          <div className="d-flex gap-2 align-items-center" style={{ flexWrap: "nowrap" }}>
            <input
              type="text"
              placeholder="Search receipt / member..."
              value={search}
              onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
              style={{
                borderRadius: 8,
                border: "1px solid #CBD5E1",
                background: "#fff",
                color: "#1E293B",
                padding: "4px 12px",
                height: 32,
                outline: "none",
                width: 220,
                fontSize: "0.84rem"
              }}
            />
            <button
              onClick={openAdd}
              style={{
                background: "#004BB5",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "0 16px",
                height: 32,
                fontWeight: 600,
                fontSize: "0.84rem",
                cursor: "pointer",
                whiteSpace: "nowrap",
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              + Add Receipt
            </button>
          </div>
        </div>
      </div>

      {/* ── Table ── */}
      <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden", marginBottom: 12 }}>
        <div className="table-responsive">
          <table className="table mb-0" style={{ fontSize: "0.88rem" }}>
            <thead style={{ background: J.primary, color: "#fff" }}>
              <tr>
                {["#", "Receipt No", "Date", "MOR", "Receipt Type", "Member / Account", "Member ID", "Loan Outstanding (₹)", "Amount (₹)", "Narration", "Actions"].map(h => (
                  <th key={h} style={{ padding: "12px 14px", fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={11} className="text-center py-6">
                  <LogoLoader size="md" />
                </td></tr>
              ) : paginated.length === 0 ? (
                <tr><td colSpan={11} className="text-center py-4 text-muted">
                  No receipts found. Click <strong>+ Add Receipt</strong> to get started.
                </td></tr>
              ) : paginated.map((item, idx) => (
                <tr key={item.id || idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                  <td style={{ padding: "12px 14px", color: "#999", fontSize: "0.82rem" }}>{(currentPage - 1) * rowsPerPage + idx + 1}</td>
                  <td style={{ padding: "12px 14px" }}><strong style={{ color: J.primary }}>RV-{item.receipt_no}</strong></td>
                  <td style={{ padding: "12px 14px" }}>{fmt(item.date)}</td>
                  <td style={{ padding: "12px 14px" }}><span style={{ background: "#e0f7fa", color: "#006064", borderRadius: 6, padding: "3px 10px", fontSize: "0.78rem", fontWeight: 600 }}>{item.mor}</span></td>
                  <td style={{ padding: "12px 14px" }}>{receiptTypeBadge(item.receipt_type, item.other_description)}</td>
                  <td style={{ padding: "12px 14px", fontWeight: 600, color: J.text }}>{item.ledger_name || "—"}</td>
                  <td style={{ padding: "12px 14px" }}><code style={{ fontSize: "0.8rem", color: J.primary }}>{item.member_unique_id || "—"}</code></td>
                  <td style={{ padding: "12px 14px" }}>₹{Number(item.loan_outstanding || 0).toLocaleString("en-IN")}</td>
                  <td style={{ padding: "12px 14px", fontWeight: 700, color: "#27ae60" }}>
                    ₹{formatINR(item.amount)}
                  </td>
                  <td style={{ padding: "12px 14px", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#666" }}>
                    {item.narration || "—"}
                  </td>
                  <td style={{ padding: "12px 14px" }}>
                    {/* Says what it does. A past receipt still opens — read-only
                        — so the button reads "View" rather than being a dead
                        "Edit" that refuses. The record stays visible; only
                        changing it is restricted. */}
                    <button
                      onClick={() => openEdit(item)}
                      title={canEdit(item) ? "" : "Dated a past day — open to read. Only an admin or superadmin can change it."}
                      style={{
                        background: J.lightBg,
                        border: `1px solid ${canEdit(item) ? J.primary : "#b0b7c3"}`,
                        color: canEdit(item) ? J.primary : "#5f6b7a",
                        borderRadius: 6,
                        padding: "4px 12px",
                        cursor: "pointer",
                        fontSize: "0.82rem",
                      }}
                    >
                      <i className={canEdit(item) ? "fas fa-edit" : "fas fa-eye"} />{" "}
                      {canEdit(item) ? "Edit" : "View"}
                    </button>
                    {isLinked(item) && (
                      <span
                        title="Created by an Inter-Branch Transfer — change it from that payment at the sending branch."
                        style={{ marginLeft: 6, fontSize: "0.72rem", background: "#eef4ff", color: "#1d4ed8", border: "1px solid #c7d7fe", borderRadius: 6, padding: "2px 8px", whiteSpace: "nowrap" }}
                      >
                        <i className="fas fa-exchange-alt" /> Inter-Branch
                      </span>
                    )}
                    {canDelete && !isLinked(item) && (
                      <button
                        onClick={() => deleteReceipt(item)}
                        title="Delete this receipt and its ledger entries"
                        style={{
                          marginLeft: 6,
                          background: "#fdecea",
                          border: "1px solid #d93025",
                          color: "#d93025",
                          borderRadius: 6,
                          padding: "4px 12px",
                          cursor: "pointer",
                          fontSize: "0.82rem",
                        }}
                      >
                        <i className="fas fa-trash" /> Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      </div>
      {/* ── Pagination ── */}
      <div style={{ padding: "6px 16px", borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6, background: "#fff", borderRadius: "0 0 12px 12px", marginTop: -1 }}>
        <div className="d-flex align-items-center gap-2 flex-nowrap" style={{ whiteSpace: "nowrap" }}>
          <span style={{ fontSize: "0.82rem", color: "#888", whiteSpace: "nowrap" }}>Rows per page:</span>
          <select value={rowsPerPage} onChange={e => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            style={{ border: `1px solid ${J.light}`, borderRadius: 6, padding: "2px 6px", height: 28, fontSize: "0.82rem", outline: "none" }}>
            {[5, 10, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div className="d-flex gap-1 flex-nowrap">
          {[["First", () => setCurrentPage(1), currentPage === 1], ["Prev", () => setCurrentPage(p => Math.max(1, p - 1)), currentPage === 1], ["Next", () => setCurrentPage(p => Math.min(totalPages, p + 1)), currentPage === totalPages], ["Last", () => setCurrentPage(totalPages), currentPage === totalPages]].map(([lbl, fn, dis]) => (
            <button key={lbl} onClick={fn} disabled={dis} style={{ border: `1px solid ${dis ? "#ddd" : J.primary}`, background: lbl === "Next" ? J.primary : "#fff", color: lbl === "Next" ? "#fff" : dis ? "#ccc" : J.primary, borderRadius: 6, padding: "3px 10px", height: 28, fontSize: "0.82rem", cursor: dis ? "not-allowed" : "pointer" }}>{lbl}</button>
          ))}
        </div>
      </div>

      {/* ── Add / Edit Modal ── */}
      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 640, maxHeight: "90vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
            <div style={{ background: `linear-gradient(135deg, ${J.primary}, ${J.accent})`, padding: "14px 20px", color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, zIndex: 1 }}>
              <h6 className="mb-0 fw-bold"><i className="fas fa-receipt me-2" />{viewOnly ? "View" : editingItem ? "Edit" : "Add"} Receipt</h6>
              <button onClick={closeModal} style={{ background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", borderRadius: 6, width: 28, height: 28, cursor: "pointer" }}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              {/* One disabled fieldset makes every control inside read-only —
                  inputs, selects, the date picker and the Clear Fields button —
                  so a new field added later is covered without being remembered.
                  A disabled fieldset also blocks submission, so the record can be
                  read but not rewritten. */}
              <fieldset disabled={viewOnly} style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}>
                {viewOnly && (
                  <div style={{ background: "#fff8e1", borderBottom: "1px solid #ffe0b2", padding: "10px 28px", fontSize: "0.82rem", color: "#8a6d3b" }}>
                    <i className="fas fa-lock me-2" />
                    {editingItem?.linked_payment_id
                      ? "Created by an Inter-Branch Transfer at the sending branch. Change or cancel it from that payment."
                      : `Dated ${formData.date} — a past day. Shown for reference; only an admin or superadmin can change it.`}
                  </div>
                )}
                <div className="modal-body" style={{ padding: "24px 28px" }}>
                  <div className="d-flex justify-content-between align-items-center mb-4 pb-2" style={{ borderBottom: "1px dashed #e0e0e0" }}>
                    <h6 className="mb-0 fw-bold" style={{ color: J.primary }}>Transaction Details</h6>
                    <button type="button" style={{ background: "#fff5f5", color: "#e74c3c", border: "1px solid #ffe3e3", borderRadius: 6, padding: "5px 14px", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer", transition: "all 0.2s" }}
                      onClick={() => setFormData(p => ({ ...p, mor: "", receipt_type: "", ledger_id: "", ledger_name: "", ledger_type: "", member_unique_id: "", loan_outstanding: "", amount: "", narration: "" }))}>
                      <i className="fas fa-eraser me-1" /> Clear Fields
                    </button>
                  </div>

                  <div className="row g-4">
                    {/* Receipt No */}
                    <div className="col-md-4">
                      <label className="form-label fw-semibold" style={{ fontSize: "0.85rem", color: "#555", marginBottom: 8 }}>Receipt No</label>
                      <div className="input-group" style={{ borderRadius: 8, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
                        <span className="input-group-text bg-light text-secondary fw-bold" style={{ border: "1px solid #dce0e5", borderRight: "none", padding: "10px 14px" }}>RV-</span>
                        <input className="form-control" value={formData.receipt_no} readOnly style={{ backgroundColor: "#f8f9fa", border: "1px solid #dce0e5", padding: "10px 14px", fontWeight: 600, color: J.primary }} />
                      </div>
                    </div>

                    {/* Date */}
                    <div className="col-md-4">
                      <label className="form-label fw-semibold" style={{ fontSize: "0.85rem", color: "#555", marginBottom: 8 }}>Date</label>
                      {/* min === max pins the picker to exactly one day while a
                        day-end blocker stands. Undefined otherwise, so ordinary
                        entry — including legitimate backdating — is untouched. */}
                      <DatePicker
                        selected={formData.date}
                        onChange={dateStr => set("date", dateStr)}
                        minDate={lockDate ? new Date(lockDate) : undefined}
                        maxDate={lockDate ? new Date(lockDate) : undefined}
                        className="form-control w-full"
                        placeholder="DD-MM-YYYY"
                        required
                      />
                      {lockDate && (
                        <div style={{ fontSize: "0.75rem", color: J.primary, marginTop: 6 }}>
                          Fixed to the day being closed — this funding belongs to that day.
                        </div>
                      )}
                    </div>

                    {/* Mode of Receiving */}
                    <div className="col-md-4">
                      <label className="form-label fw-semibold" style={{ fontSize: "0.85rem", color: "#555", marginBottom: 8 }}>Mode of Receiving</label>
                      <select className="form-select" value={formData.mor}
                        onChange={e => {
                          const nextMor = e.target.value;
                          setFormData(p => ({
                            ...p,
                            mor: nextMor,
                            // The mode decides the DESTINATION, which decides which
                            // accounts may be the source — the destination itself is
                            // excluded from the Ledger list. A source chosen under
                            // the old mode may now BE the destination, so drop it
                            // rather than leave a pairing the backend would refuse.
                            // Member borrowing has no source account, so it is left
                            // alone.
                            ...(isMemberBorrowing
                              ? {}
                              : { ledger_id: "", ledger_name: "", receipt_type: "", loan_outstanding: "" }),
                          }));
                        }}
                        required style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid #dce0e5", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
                        <option value="">-- Select MOR --</option>
                        {(BANK_ONLY_RECEIPT_TYPES.includes(formData.receipt_type)
                          ? MOR_OPTIONS.filter(m => m !== "CASH")
                          : MOR_OPTIONS
                        ).map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                      {/* A missing option with no reason reads as a bug. */}
                      {BANK_ONLY_RECEIPT_TYPES.includes(formData.receipt_type) && (
                        <div style={{ fontSize: "0.75rem", color: "#777", marginTop: 6 }}>
                          Settles in the bank — loans are disbursed from the bank.
                        </div>
                      )}
                      {/* MOR means "where it lands", which is not obvious when the
                        same screen records transfers and investments as well as
                        money arriving from outside. */}
                      {!isMemberBorrowing && formData.mor && (
                        <div style={{ fontSize: "0.75rem", color: J.primary, marginTop: 6 }}>
                          Money lands in {formData.mor === "CASH" ? "Cash" : "the Bank"}.
                        </div>
                      )}
                    </div>

                    {/* LEDGER — where the money came FROM. Mode of Receiving above
                      says where it lands, and the two together are the whole
                      entry. This replaced "Receipt Type", which named an account
                      group and was the same question asked less directly. */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold" style={{ fontSize: "0.85rem", color: "#555", marginBottom: 8 }}>
                        Ledger <span className="text-danger">*</span>
                      </label>
                      <select className="form-select"
                        value={isMemberBorrowing ? MEMBER_BORROWING_TYPE : formData.ledger_id}
                        required
                        onChange={e => {
                          const v = e.target.value;

                          // The members'-savings case carries no source account: it
                          // posts Dr Member Savings / Cr Borrowings from Members and
                          // moves no cash. The backend keys on receipt_type for it,
                          // so ledger_id is deliberately left empty.
                          if (v === MEMBER_BORROWING_TYPE) {
                            setFormData(p => ({
                              ...p, receipt_type: MEMBER_BORROWING_TYPE,
                              ledger_id: "", ledger_name: "", ledger_type: "",
                              member_unique_id: "", loan_outstanding: "", other_description: "",
                            }));
                            return;
                          }

                          const acc = ledgerOptions.find(a => String(a.id) === v);
                          setFormData(p => ({
                            ...p,
                            ledger_id: v,
                            ledger_name: acc ? acc.name : "",
                            ledger_type: "ledger",
                            // receipt_type still carries the GROUP name. The backend
                            // no longer dispatches on it, but the list column and
                            // existing rows read it, so it stays meaningful.
                            receipt_type: acc ? acc.groupName : "",
                            member_unique_id: "", loan_outstanding: "", other_description: "",
                          }));
                          // Cash and Bank sources show the branch's own balance.
                          if (acc && (String(acc.groupName || "").trim().toLowerCase() === "cash in hand" ||
                            String(acc.groupName || "").trim().toLowerCase() === "bank accounts")) {
                            fetchAccountBalance(acc.groupName);
                          }
                        }}
                        style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid #dce0e5", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
                        <option value="">-- Select Ledger --</option>
                        {/* Not an account, so it sits apart from them. */}
                        <option value={MEMBER_BORROWING_TYPE}>{MEMBER_BORROWING_TYPE} (lend members' savings)</option>
                        <optgroup label="────────── Ledgers ──────────">
                          {ledgerOptions.map(a => (
                            <option key={a.id} value={a.id}>{a.name}</option>
                          ))}
                        </optgroup>
                      </select>
                      {ledgerOptions.length === 0 && (
                        <small className="text-danger mt-1 d-block" style={{ fontSize: "0.8rem" }}>
                          No ledger accounts for this branch. Run scripts/create_branch_ledger_accounts.js.
                        </small>
                      )}
                      {/* State the entry in words. "Receipt" is a thin description
                        once the screen also records transfers and investments. */}
                      {formData.ledger_name && formData.mor && (
                        <div style={{ fontSize: "0.75rem", color: J.primary, marginTop: 6 }}>
                          {formData.mor === "CASH" ? "Cash" : "Bank"} rises; {formData.ledger_name} falls.
                        </div>
                      )}
                    </div>

                    {/* Member / Account - Search popup trigger */}
                    {isMemberGroup && (
                      <div className="col-md-6">
                        <label className="form-label fw-semibold" style={{ fontSize: "0.85rem", color: "#555", marginBottom: 8 }}>
                          Member / Account <span className="text-danger">*</span>
                        </label>
                        <div className="input-group" style={{ borderRadius: 8, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
                          <input className="form-control" value={formData.ledger_name} readOnly
                            placeholder="Search & select member or account..."
                            style={{ cursor: "pointer", backgroundColor: formData.ledger_name ? "#f0fff4" : "#fff", border: "1px solid #dce0e5", padding: "10px 14px", fontWeight: formData.ledger_name ? 600 : 400 }}
                            onClick={() => setShowSearch(true)} />
                          <button type="button" style={{ background: J.primary, border: "1px solid " + J.primary, color: "#fff", padding: "0 18px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowSearch(true)}>
                            <i className="fas fa-search" />
                          </button>
                        </div>
                        {formData.member_unique_id && (
                          <small className="text-muted mt-1 d-block" style={{ fontSize: "0.8rem" }}>Selected ID: <strong style={{ color: J.primary }}>{formData.member_unique_id}</strong></small>
                        )}
                      </div>
                    )}

                    {/* Loan Outstanding - auto-fetched. A borrowing has no member
                      and therefore no outstanding to fetch.

                      A TRANSFER shows the same field for a different quantity:
                      what the SOURCE ledger currently holds. Read-only, because
                      unlike a member's outstanding it is not an input — it is the
                      branch's own balance, and typing over it would state
                      something untrue about the ledger. */}
                    {(isMemberGroup || isOwnFundsSource) && (
                      <div className="col-md-6">
                        <label className="form-label fw-semibold" style={{ fontSize: "0.85rem", color: "#555", marginBottom: 8 }}>
                          {isOwnFundsSource ? "Available in this ledger (₹)" : "Outstanding Amount (₹)"}
                          {outstandingLoading && <LogoLoader size="xs" className="inline-flex ms-2 p-0" />}

                          {formData.ledger_name && !outstandingLoading && (
                            <span className="ms-2 badge" style={{ background: "#e8f5e9", color: "#2e7d32", fontWeight: 500, fontSize: "0.72rem", borderRadius: 6 }}>Auto-fetched</span>
                          )}
                        </label>
                        <input type="number" className="form-control" value={formData.loan_outstanding}
                          onChange={e => set("loan_outstanding", e.target.value)}
                          readOnly={isOwnFundsSource}
                          placeholder={
                            isOwnFundsSource
                              ? (formData.ledger_name ? "Reading balance..." : "Select the source ledger first")
                              : (formData.ledger_name ? "Fetching outstanding..." : "Select member/account first")
                          }
                          min="0" step="0.01"
                          style={{ padding: "10px 14px", borderRadius: 8, border: formData.loan_outstanding ? "1px solid #27ae60" : "1px solid #dce0e5", backgroundColor: isOwnFundsSource ? "#eef2fb" : (formData.loan_outstanding ? "#f0fff4" : "#f8f9fa"), boxShadow: "0 1px 3px rgba(0,0,0,0.02)", fontWeight: formData.loan_outstanding ? 600 : 400, color: formData.loan_outstanding ? "#2e7d32" : "#999" }} />
                      </div>
                    )}

                    {/* Amount */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold" style={{ fontSize: "0.85rem", color: "#555", marginBottom: 8 }}>Amount Received (₹) <span className="text-danger">*</span></label>
                      <input type="number" className="form-control" value={formData.amount}
                        onChange={e => set("amount", e.target.value)}
                        placeholder="Enter amount received" required min="1" step="0.01"
                        style={{ padding: "10px 14px", borderRadius: 8, border: "2px solid #27ae60", backgroundColor: "#fafcfb", fontWeight: 600, color: "#27ae60", boxShadow: "0 2px 6px rgba(39,174,96,0.15)" }} />
                    </div>

                    {/* Narration */}
                    <div className="col-12">
                      <label className="form-label fw-semibold" style={{ fontSize: "0.85rem", color: "#555", marginBottom: 8 }}>Narration / Remarks</label>
                      <textarea className="form-control" rows={2} value={formData.narration}
                        onChange={e => set("narration", e.target.value)}
                        placeholder="e.g. EMI for April 2026 – Loan A/c 12345" style={{ padding: "12px 14px", borderRadius: 8, border: "1px solid #dce0e5", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }} />
                    </div>
                  </div>
                </div>
              </fieldset>
              {/* Outside the fieldset: closing the window must work in view mode. */}
              <div style={{ padding: "16px 28px", borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "flex-end", gap: 12, background: "#fafafa", borderRadius: "0 0 14px 14px" }}>
                <button type="button" onClick={closeModal} style={{ border: "1px solid #ddd", background: "#fff", color: "#666", borderRadius: 8, padding: "8px 24px", cursor: "pointer" }}>{viewOnly ? "Close" : "Cancel"}</button>
                {!viewOnly && (
                  <button type="submit" disabled={loading} style={{ background: `linear-gradient(135deg, ${J.primary}, ${J.accent})`, border: "none", color: "#fff", borderRadius: 8, padding: "8px 32px", fontWeight: 700, cursor: loading ? "not-allowed" : "pointer" }}>
                    {loading ? "Saving..." : "Save Receipt"}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Ledger / Member Search Popup ── */}
      {showSearch && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 520, boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
            <div style={{ background: `linear-gradient(135deg, ${J.primary}, ${J.accent})`, padding: "12px 18px", color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center", borderRadius: "14px 14px 0 0" }}>
              <h6 className="mb-0 fw-bold"><i className="fas fa-search me-2" />Search Member / Staff / Account</h6>
              <button onClick={() => { setShowSearch(false); setLedgerQuery(""); }} style={{ background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", borderRadius: 6, width: 28, height: 28, cursor: "pointer" }}>✕</button>
            </div>
            <div style={{ padding: 12 }}>
              <input type="text" className="form-control mb-2" placeholder="Type name, ID or designation..."
                value={ledgerQuery} onChange={e => setLedgerQuery(e.target.value)} autoFocus />
              <div style={{ maxHeight: 360, overflowY: "auto" }}>
                <table className="table table-sm table-hover mb-0">
                  <thead style={{ background: J.lightBg, position: "sticky", top: 0 }}>
                    <tr><th>Name</th><th>ID / Code</th><th>Type</th></tr>
                  </thead>
                  <tbody>
                    {searchEntries.length === 0 ? (
                      <tr><td colSpan={3} className="text-center text-muted py-3">No results found.</td></tr>
                    ) : searchEntries.map(e => (
                      <tr key={`${e.type}_${e.id}`} style={{ cursor: "pointer" }} onClick={() => selectLedger(e)}>
                        <td style={{ fontWeight: 600, color: J.text }}>{e.label}</td>
                        <td><code style={{ fontSize: "0.78rem", color: J.primary }}>{e.sub || "—"}</code></td>
                        <td><span style={{ background: e.type === "member" ? "#fff3cd" : e.type === "staff" ? "#dbeafe" : J.lightBg, color: e.type === "member" ? "#856404" : e.type === "staff" ? "#1d4ed8" : J.primary, border: `1px solid ${e.type === "member" ? "#ffc107" : e.type === "staff" ? "#93c5fd" : J.light}`, borderRadius: 6, padding: "2px 8px", fontSize: "0.75rem", fontWeight: 600 }}>{e.type === "member" ? "Member" : e.type === "staff" ? "Staff" : "Account"}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Receipts;
